import React, { useState, useEffect, useCallback } from 'react';
import { Printer, RefreshCw, FileText, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

/**
 * Public Mobile-First Digital Customer Ledger Statement Viewer
 * Accessible directly by customers via WhatsApp link (No login required)
 * Features:
 *  - 100% responsive, authentic standard 5-column bank statement format
 *  - Single-line compact particulars (no awkward text wrapping)
 *  - 1-Click "प्रिंट / PDF डाऊनलोड" button for saving crisp A4 PDF
 */
export default function PublicStatementView({ lang = 'mr' }) {
  const isMr = lang === 'mr';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);

  // Extract params from hash: #statement-view?t=slug&c=custId&from=2026-09-01&to=2026-09-23
  const parseParams = () => {
    const hash = window.location.hash || '';
    const queryPart = hash.includes('?') ? hash.split('?')[1] : '';
    const params = new URLSearchParams(queryPart);

    const now = new Date();
    const defaultFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const defaultTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    return {
      slug: params.get('t') || params.get('tenant') || 'samarth-krupa-dairy',
      customerId: params.get('c') || params.get('cust') || '1',
      fromDate: params.get('from') || defaultFrom,
      toDate: params.get('to') || defaultTo
    };
  };

  const { slug, customerId, fromDate, toDate } = parseParams();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch Tenant details
      const tenantRes = await fetch(`${API_BASE}/tenant/by-slug/${slug}`);
      const tenantJson = await tenantRes.json();
      if (tenantJson.success && tenantJson.tenant) {
        setTenantInfo(tenantJson.tenant);
      }

      // 2. Fetch Ledger details
      const ledgerUrl = `${API_BASE}/tenant/${slug}/customers/${customerId}/ledger?from_date=${fromDate}&to_date=${toDate}`;
      const ledgerRes = await fetch(ledgerUrl);
      const ledgerJson = await ledgerRes.json();

      if (ledgerJson.success) {
        setLedgerData(ledgerJson);
      } else {
        setError(ledgerJson.message || 'खातेउतारा उपलब्ध होऊ शकला नाही.');
      }
    } catch (err) {
      console.error('Failed to load statement:', err);
      setError('सर्व्हरशी संपर्क होऊ शकला नाही. कृपया थोड्या वेळाने पुन्हा प्रयत्न करा.');
    } finally {
      setLoading(false);
    }
  }, [slug, customerId, fromDate, toDate]);

  const handleDeleteEntry = async (item) => {
    const dFmt = item.date.split('-').reverse().join('/');
    const confirmMsg = isMr
      ? '⚠️ खात्री करा:\nदिनांक ' + dFmt + ' ची \'' + item.description + '\' ही ₹' + (item.debit || item.credit) + ' ची नोंद खातेउताऱ्यातून कायमची हटवायची आहे का?\n\n(ही नोंद हटवल्यास रोजनिशी व मासिक हजेरीतून ती आपोआप रद्द होईल.)'
      : 'Are you sure you want to permanently delete the entry for ' + dFmt + ' (' + item.description + ')?';

    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await fetch(`${API_BASE}/tenant/${slug}/billing/delete-entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          date: item.date,
          category: item.category,
          type: item.type
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || 'नोंद यशस्वीरित्या हटवली.');
        loadData();
      } else {
        alert(data.message || 'नोंद हटवण्यात त्रुटी आली.');
      }
    } catch (err) {
      alert('सर्व्हरशी संपर्क होऊ शकला नाही: ' + err.message);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const dairyName = isMr 
    ? (tenantInfo?.brand_name_mr || tenantInfo?.brandMarathi || tenantInfo?.name || 'समर्थ कृपा डेअरी')
    : (tenantInfo?.brand_name_en || tenantInfo?.brandEnglish || tenantInfo?.name || 'Samarth Krupa Dairy');

  const dairyAddress = tenantInfo?.address || tenantInfo?.city || '';
  const dairyPhone = tenantInfo?.phone || '';

  const cust = ledgerData?.customer;
  const customerName = isMr
    ? (cust?.name_mr || cust?.name || `ग्राहक #${customerId}`)
    : (cust?.name_en || cust?.name || `Customer #${customerId}`);

  const fromFmt = fromDate.split('-').reverse().join('/');
  const toFmt = toDate.split('-').reverse().join('/');

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f1f5f9',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      <style>{`
        @media print {
          body {
            background: #ffffff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .no-print {
            display: none !important;
          }
          #statement-sheet {
            box-shadow: none !important;
            border: none !important;
            max-width: 100% !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .table-bordered th, .table-bordered td {
            border: 1px solid #475569 !important;
            padding: 4px 6px !important;
            font-size: 11px !important;
          }
        }
      `}</style>

      {/* TOP FLOATING ACTION BAR FOR MOBILE / DESKTOP (Hidden on Print) */}
      <div className="no-print" style={{
        width: '100%',
        maxWidth: 820,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
        gap: 8,
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={20} color="#0284c7" />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>
            {isMr ? 'ग्राहक अधिकृत खातेउतारा' : 'Customer Account Statement'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={loadData}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#334155',
              fontWeight: 700,
              fontSize: '0.8rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5
            }}
          >
            <RefreshCw size={13} className={loading ? 'spin' : ''} />
            <span>{isMr ? 'रिफ्रेश' : 'Refresh'}</span>
          </button>

          <button
            type="button"
            onClick={() => window.print()}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#0284c7',
              color: '#ffffff',
              fontWeight: 800,
              fontSize: '0.84rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 8px rgba(2, 132, 199, 0.3)'
            }}
          >
            <Printer size={15} />
            <span>{isMr ? 'प्रिंट / PDF सेव्ह करा' : 'Print / Save PDF'}</span>
          </button>
        </div>
      </div>

      {/* MAIN STATEMENT PAPER CONTAINER */}
      <div 
        id="statement-sheet"
        style={{
          width: '100%',
          maxWidth: 820,
          background: '#ffffff',
          borderRadius: 12,
          padding: '20px 24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid #cbd5e1',
          boxSizing: 'border-box'
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
            <RefreshCw size={30} className="spin" style={{ margin: '0 auto 10px auto', color: '#0284c7' }} />
            <div style={{ fontWeight: 800 }}>{isMr ? 'खातेउतारा लोड होत आहे...' : 'Loading statement...'}</div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#b91c1c' }}>
            <AlertCircle size={32} style={{ margin: '0 auto 8px auto' }} />
            <div style={{ fontWeight: 800, fontSize: '1rem' }}>{error}</div>
          </div>
        ) : (
          <>
            {/* 1. DAIRY & STATEMENT HEADER */}
            <div style={{ borderBottom: '2px solid #0f172a', paddingBottom: 10, textAlign: 'center' }}>
              <h1 style={{ margin: '0 0 4px 0', fontSize: '1.45rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.3px' }}>
                ॥ {dairyName} ॥
              </h1>
              <div style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600 }}>
                {dairyAddress} {dairyPhone ? `| मो. ${dairyPhone}` : ''}
              </div>
              <div style={{
                marginTop: 6,
                display: 'inline-block',
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                padding: '2px 14px',
                borderRadius: 4,
                fontSize: '0.88rem',
                fontWeight: 900,
                color: '#0f172a',
                textTransform: 'uppercase'
              }}>
                {isMr ? 'ग्राहक खातेउतारा (STATEMENT OF ACCOUNT)' : 'STATEMENT OF ACCOUNT'}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 700, marginTop: 4 }}>
                {isMr ? `कालावधी: ${fromFmt} ते ${toFmt}` : `Period: ${fromFmt} to ${toFmt}`}
              </div>
            </div>

            {/* 2. CUSTOMER & ACCOUNT DETAILS STRIP */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: '10px 12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              marginTop: 10,
              fontSize: '0.82rem',
              lineHeight: 1.5
            }}>
              <div>
                <div><strong>{isMr ? 'ग्राहक नाव:' : 'Customer Name:'}</strong> <span style={{ fontSize: '0.92rem', fontWeight: 900, color: '#0f172a' }}>{customerName}</span></div>
                <div><strong>{isMr ? 'ग्राहक आयडी (ID):' : 'Customer ID:'}</strong> #{customerId}</div>
                <div><strong>{isMr ? 'पत्ता:' : 'Address:'}</strong> {cust?.address || '-'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div><strong>{isMr ? 'मोबाईल:' : 'Phone:'}</strong> {cust?.phone || '-'}</div>
                <div><strong>{isMr ? 'मार्ग / लाईन:' : 'Line:'}</strong> {cust?.line_name || '-'}</div>
                <div><strong>{isMr ? 'तारीख:' : 'Date:'}</strong> {new Date().toLocaleDateString('en-GB')}</div>
              </div>
            </div>

            {/* 3. KEY BALANCES SUMMARY STRIP */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
              marginTop: 10,
              marginBottom: 12,
              textAlign: 'center'
            }}>
              <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', padding: '6px 8px', borderRadius: 6 }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>{isMr ? 'आरंभी बाकी (Opening)' : 'Opening Bal'}</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 900, color: '#0369a1' }}>₹{Number(ledgerData.opening_balance).toLocaleString()} Dr</div>
              </div>
              <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', padding: '6px 8px', borderRadius: 6 }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>{isMr ? 'एकूण नावे (Total Dr)' : 'Total Debit'}</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 900, color: '#4338ca' }}>₹{Number(ledgerData.total_debits).toLocaleString()}</div>
              </div>
              <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', padding: '6px 8px', borderRadius: 6 }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>{isMr ? 'एकूण जमा (Total Cr)' : 'Total Credit'}</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 900, color: '#15803d' }}>₹{Number(ledgerData.total_credits).toLocaleString()}</div>
              </div>
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', padding: '6px 8px', borderRadius: 6 }}>
                <div style={{ fontSize: '0.7rem', color: '#991b1b', fontWeight: 700 }}>{isMr ? 'अंतिम बाकी (Net Due)' : 'Closing Balance'}</div>
                <div style={{ fontSize: '0.96rem', fontWeight: 900, color: '#b91c1c' }}>₹{Number(ledgerData.closing_balance).toLocaleString()} Dr</div>
              </div>
            </div>

            {/* 4. STANDARD 5-COLUMN ACCOUNT LEDGER TABLE */}
            <table className="table-bordered" style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.8rem',
              lineHeight: 1.3
            }}>
              <thead>
                <tr style={{ background: '#0f172a', color: '#ffffff', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px', fontWeight: 800, width: 85, border: '1px solid #0f172a' }}>
                    {isMr ? 'दिनांक' : 'Date'}
                  </th>
                  <th style={{ padding: '6px 8px', fontWeight: 800, border: '1px solid #0f172a' }}>
                    {isMr ? 'तपशील / विवरण (Particulars)' : 'Particulars'}
                  </th>
                  <th style={{ padding: '6px 8px', fontWeight: 800, textAlign: 'right', width: 95, border: '1px solid #0f172a' }}>
                    {isMr ? 'नावे (Dr ₹)' : 'Debit (₹)'}
                  </th>
                  <th style={{ padding: '6px 8px', fontWeight: 800, textAlign: 'right', width: 95, border: '1px solid #0f172a' }}>
                    {isMr ? 'जमा (Cr ₹)' : 'Credit (₹)'}
                  </th>
                  <th style={{ padding: '6px 8px', fontWeight: 800, textAlign: 'right', width: 110, border: '1px solid #0f172a' }}>
                    {isMr ? 'शिल्लक बाकी' : 'Balance (₹)'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* 1. Opening Balance Row */}
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1', fontWeight: 800 }}>
                  <td style={{ padding: '5px 8px', color: '#475569', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                    {fromFmt}
                  </td>
                  <td style={{ padding: '5px 8px', color: '#0f172a', border: '1px solid #e2e8f0' }}>
                    {isMr ? `आरंभी बाकी (Opening Balance - ${fromFmt} पूर्वीची शिल्लक)` : `Opening Balance (Prior to ${fromFmt})`}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', color: '#0369a1', border: '1px solid #e2e8f0' }}>
                    ₹{Number(ledgerData.opening_balance).toLocaleString()}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', color: '#94a3b8', border: '1px solid #e2e8f0' }}>-</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', color: '#0369a1', fontWeight: 900, border: '1px solid #e2e8f0' }}>
                    ₹{Number(ledgerData.opening_balance).toLocaleString()} Dr
                  </td>
                </tr>

                {/* 2. Chronological Transactions (Filtered without 0L/खाडा) */}
                {ledgerData.entries && ledgerData.entries.length > 0 ? (
                  ledgerData.entries.map((item, idx) => {
                    const isDebit = item.type === 'DEBIT';
                    const isCredit = item.type === 'CREDIT';

                    const rowBg = isCredit ? '#f0fdf4' : (idx % 2 === 0 ? '#ffffff' : '#fcfcfc');
                    const dFmt = item.date.split('-').reverse().join('/');

                    return (
                      <tr key={idx} style={{ background: rowBg, borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '5px 8px', fontWeight: 700, color: '#334155', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                          {dFmt}
                        </td>
                        <td style={{ 
                          padding: '5px 8px', 
                          color: '#0f172a', 
                          border: '1px solid #e2e8f0',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 360
                        }} title={item.description}>
                          {item.description}
                        </td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 800, color: isDebit && item.debit > 0 ? '#4338ca' : '#94a3b8', border: '1px solid #e2e8f0' }}>
                          {isDebit && item.debit > 0 ? `₹${Number(item.debit).toLocaleString()}` : '-'}
                        </td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 800, color: isCredit && item.credit > 0 ? '#15803d' : '#94a3b8', border: '1px solid #e2e8f0' }}>
                          {isCredit && item.credit > 0 ? `₹${Number(item.credit).toLocaleString()}` : '-'}
                        </td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 900, color: (item.balance || 0) > 0 ? '#b91c1c' : '#15803d', border: '1px solid #e2e8f0' }}>
                          ₹{Number(item.balance || 0).toLocaleString()} {(item.balance || 0) > 0 ? 'Dr' : ''}
                        </td>
                        <td className="no-print" style={{ padding: '3px 4px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                          <button
                            type="button"
                            onClick={() => handleDeleteEntry(item)}
                            style={{
                              background: '#fee2e2',
                              border: '1px solid #fca5a5',
                              color: '#dc2626',
                              borderRadius: 5,
                              width: 26,
                              height: 26,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                            title="नोंद हटवा"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', border: '1px solid #e2e8f0' }}>
                      {isMr ? 'या कालावधीत कोणतेही व्यवहार नाहीत.' : 'No transactions in this period.'}
                    </td>
                  </tr>
                )}

                {/* 3. Totals & Closing Row */}
                <tr style={{ background: '#f8fafc', borderTop: '2px solid #0f172a', fontWeight: 900 }}>
                  <td colSpan={2} style={{ padding: '8px', textAlign: 'right', color: '#0f172a', border: '1px solid #cbd5e1' }}>
                    {isMr ? `एकूण व्यवहार व अंतिम बाकी (${toFmt} अखेर):` : `Total & Closing Balance (As of ${toFmt}):`}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#4338ca', border: '1px solid #cbd5e1' }}>
                    ₹{Number(ledgerData.total_debits).toLocaleString()}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#15803d', border: '1px solid #cbd5e1' }}>
                    ₹{Number(ledgerData.total_credits).toLocaleString()}
                  </td>
                  <td style={{ 
                    padding: '8px', 
                    textAlign: 'right', 
                    fontSize: '0.94rem', 
                    color: ledgerData.closing_balance > 0 ? '#b91c1c' : '#15803d',
                    border: '1px solid #cbd5e1'
                  }}>
                    ₹{Number(ledgerData.closing_balance).toLocaleString()} {ledgerData.closing_balance > 0 ? 'Dr' : ''}
                  </td>
                  <td className="no-print" style={{ border: '1px solid #cbd5e1' }}></td>
                </tr>
              </tbody>
            </table>

            {/* 5. FOOTER & SIGNATURE */}
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '0.8rem', color: '#475569' }}>
              <div>
                <div><strong>अंतिम बाकी अक्षरी:</strong> {Number(ledgerData.closing_balance) > 0 ? `रुपये ${Number(ledgerData.closing_balance).toLocaleString()} फक्त येणे बाकी.` : 'पूर्ण हिशोब चुकता (काहीही बाकी नाही).'}</div>
                <div style={{ marginTop: 2, fontSize: '0.72rem' }}>* संगणकीकृत अधिकृत खातेउतारा | e.Sense MilkPro Mitra</div>
              </div>
              <div style={{ textAlign: 'center', borderTop: '1px solid #64748b', width: 170, paddingTop: 4 }}>
                <strong>॥ {dairyName} ॥</strong><br />
                <span style={{ fontSize: '0.75rem' }}>अधिकृत स्वाक्षरी / शिक्का</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
